import { useCallback, useEffect, useMemo, useState } from "react";
import { Redirect, useHistory } from "react-router";
import { useTranslation } from "react-i18next";
import { Card } from "reactstrap";
import Cookies from "js-cookie";
import PageHeader from "../../components/PageHeader";
import StandardTable from "../../ui/components/StandardTable";
import Pagination from "../../components/utils/Pagination";
import EmptyState from "../../components/EmptyState";
import { InlineLoader } from "../../components/loader/CharacterizationLoader";
import { PeriodFilterModal } from "../../ui/components/filters";
import CampaignStatusTabs from "./components/CampaignStatusTabs";
import CampaignListRow from "./components/CampaignListRow";
import CampaignDeleteModal from "./CampaignDeleteModal";
import ForbiddenModule from "../../components/ForbiddenModule";
import { useCampaignListData } from "./hooks/useCampaignListData";
import {
  buildDuplicateCampaign,
  toCampaignFormModel,
} from "./campaignDuplicateUtils";

const SINCE_BEGINNING_START = new Date(2021, 3, 26);

const TableHeaderCell = ({ label, columnKey, sortConfig, onSort }) => {
  const isActive = sortConfig.column === columnKey;
  const directionIcon = isActive
    ? sortConfig.direction === "asc"
      ? "fa-sort-up"
      : "fa-sort-down"
    : "fa-sort";
  return (
    <div
      className="table-standard-header-cell"
      style={{ cursor: "pointer" }}
      onClick={() => onSort(columnKey)}
      onKeyDown={(e) => e.key === "Enter" && onSort(columnKey)}
      role="button"
      tabIndex={0}
    >
      {label}
      <span className="sort-icon">
        <i className={`fas ${directionIcon}`} aria-hidden />
      </span>
    </div>
  );
};

const CampaignList = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const token = Cookies.get("userToken");

  const corporationModules = useMemo(() => {
    const stored = localStorage.getItem("corporationModules");
    return stored ? JSON.parse(stored) : null;
  }, []);

  const [campaignAllowed, setCampaignAllowed] = useState(true);
  const [searchValue, setSearchValue] = useState("");
  const [periodFilter, setPeriodFilter] = useState(null);
  const [statusTabFilter, setStatusTabFilter] = useState("all");
  const [sortConfig, setSortConfig] = useState({
    column: "stop",
    direction: "desc",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [recordPerPage, setRecordPerPage] = useState(10);
  const [resultPage, setResultPage] = useState(0);

  const [isPeriodModalOpen, setIsPeriodModalOpen] = useState(false);
  const [periodDisplayLabel, setPeriodDisplayLabel] = useState(() =>
    t("Since the beginning"),
  );
  const [periodStartDate, setPeriodStartDate] = useState(null);
  const [periodEndDate, setPeriodEndDate] = useState(null);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [campaignToDelete, setCampaignToDelete] = useState(null);

  const viewMode = Cookies.get("viewMode") || "client";
  const isAkanthasAccount = (Cookies.get("userEmail") || "").includes(
    "@akanthas.com",
  );

  const { periodMaterialStart, periodMaterialStop } = useMemo(() => {
    const now = new Date();
    now.setHours(23, 59, 59, 999);
    if (periodStartDate && periodEndDate) {
      const start = new Date(periodStartDate);
      start.setHours(0, 0, 0, 0);
      const end = new Date(periodEndDate);
      end.setHours(23, 59, 59, 999);
      return {
        periodMaterialStart: start.toISOString(),
        periodMaterialStop: end.toISOString(),
      };
    }
    if (periodFilter === null) {
      const start = new Date(SINCE_BEGINNING_START);
      start.setHours(0, 0, 0, 0);
      return {
        periodMaterialStart: start.toISOString(),
        periodMaterialStop: now.toISOString(),
      };
    }
    const months = Number(periodFilter);
    if (isNaN(months) || months <= 0) {
      const start = new Date(SINCE_BEGINNING_START);
      start.setHours(0, 0, 0, 0);
      return {
        periodMaterialStart: start.toISOString(),
        periodMaterialStop: now.toISOString(),
      };
    }
    const startDate = new Date(now);
    startDate.setMonth(startDate.getMonth() - Math.ceil(months));
    startDate.setHours(0, 0, 0, 0);
    return {
      periodMaterialStart: startDate.toISOString(),
      periodMaterialStop: now.toISOString(),
    };
  }, [periodFilter, periodStartDate, periodEndDate]);

  const { campaigns, isLoading, fetchCampaigns } = useCampaignListData(
    periodMaterialStart,
    periodMaterialStop,
  );

  useEffect(() => {
    if (corporationModules !== null) {
      setCampaignAllowed(corporationModules?.showCampaignModule || false);
    }
  }, [corporationModules]);

  const statusCounts = useMemo(
    () => ({
      all: campaigns.length,
      InProgress: campaigns.filter((c) => c.status === "InProgress").length,
      Scheduled: campaigns.filter((c) => c.status === "Scheduled").length,
      Completed: campaigns.filter((c) => c.status === "Completed").length,
    }),
    [campaigns],
  );

  const filteredCampaigns = useMemo(() => {
    const searchLower = searchValue.trim().toLowerCase();
    return campaigns.filter((campaign) => {
      if (statusTabFilter !== "all" && campaign.status !== statusTabFilter) {
        return false;
      }
      if (!searchLower) return true;
      const addressName = campaign.addressName?.toLowerCase() ?? "";
      const addressCity = campaign.addressCity?.toLowerCase() ?? "";
      const zoneLabel = campaign.zoneLabel?.toLowerCase() ?? "";
      const flowLabel = campaign.flowLabel?.toLowerCase() ?? "";
      const containerInfos =
        campaign.containerInfos?.join(", ").toLowerCase() ?? "";
      return (
        addressName.includes(searchLower) ||
        addressCity.includes(searchLower) ||
        zoneLabel.includes(searchLower) ||
        flowLabel.includes(searchLower) ||
        containerInfos.includes(searchLower)
      );
    });
  }, [campaigns, searchValue, statusTabFilter]);

  const sortedCampaigns = useMemo(() => {
    const sorted = [...filteredCampaigns];
    sorted.sort((a, b) => {
      const multiplier = sortConfig.direction === "asc" ? 1 : -1;
      switch (sortConfig.column) {
        case "address":
          return (
            multiplier *
            (a.addressName ?? "").localeCompare(b.addressName ?? "")
          );
        case "status":
          return multiplier * (a.status ?? "").localeCompare(b.status ?? "");
        case "errors":
          return multiplier * ((a.totalTreated ?? 0) - (b.totalTreated ?? 0));
        case "stop":
        default: {
          const stopDiff =
            (a.stopDate?.getTime() ?? 0) - (b.stopDate?.getTime() ?? 0);
          if (stopDiff !== 0) {
            return multiplier * stopDiff;
          }
          return (
            multiplier *
            ((a.startDate?.getTime() ?? 0) - (b.startDate?.getTime() ?? 0))
          );
        }
        case "start":
          return (
            multiplier *
            ((a.startDate?.getTime() ?? 0) - (b.startDate?.getTime() ?? 0))
          );
      }
    });
    return sorted;
  }, [filteredCampaigns, sortConfig]);

  const paginatedCampaigns = useMemo(() => {
    const start = (currentPage - 1) * recordPerPage;
    return sortedCampaigns.slice(start, start + recordPerPage);
  }, [currentPage, recordPerPage, sortedCampaigns]);

  useEffect(() => {
    setCurrentPage(1);
  }, [sortedCampaigns.length, recordPerPage]);

  const hasCampaigns = paginatedCampaigns.length > 0;
  const hasAnyCampaigns = campaigns.length > 0;
  const hasActiveFilters = useMemo(
    () =>
      Boolean(searchValue?.trim()) ||
      statusTabFilter !== "all" ||
      periodStartDate != null ||
      periodFilter !== null,
    [searchValue, statusTabFilter, periodStartDate, periodFilter],
  );
  const showTableChrome = hasAnyCampaigns || hasActiveFilters;
  const showEmptyState = !isLoading && !hasAnyCampaigns && !hasActiveFilters;

  const handleSort = useCallback((column) => {
    setSortConfig((prev) => {
      if (prev.column === column) {
        return {
          column,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      return { column, direction: "asc" };
    });
  }, []);

  const openCreatePage = useCallback(() => {
    history.push("/campaign/create");
  }, [history]);

  const handleEditCampaign = useCallback(
    (campaign) => {
      history.push(`/campaign/${campaign._id}/edit`, {
        campaign: toCampaignFormModel(campaign),
      });
    },
    [history],
  );

  const handleDuplicateCampaign = useCallback(
    (campaign) => {
      history.push("/campaign/create", {
        duplicateTemplate: buildDuplicateCampaign(campaign),
      });
    },
    [history],
  );

  const handleDeleteCampaign = useCallback((campaign) => {
    setCampaignToDelete(campaign);
    setDeleteModalOpen(true);
  }, []);

  const createCampaignButton = (
    <button
      type="button"
      className="create-button campaign-list-create-button"
      onClick={openCreatePage}
    >
      {t("Create a campaign")}
    </button>
  );

  if (!token) {
    return <Redirect to={{ pathname: "/login" }} />;
  }

  if (!campaignAllowed && !(isAkanthasAccount && viewMode === "admin")) {
    return (
      <div className="settings-page-wrapper">
        <PageHeader title={t("Sorting campaign")} />
        <div className="settings-content-wrapper settings-content-wrapper--campaign-list">
          <ForbiddenModule viewType="campaign" />
        </div>
      </div>
    );
  }

  return (
    <div className="settings-page-wrapper">
      <PageHeader
        title={t("Sorting campaign")}
        actions={showEmptyState ? null : createCampaignButton}
      />
      <div
        className={`settings-content-wrapper settings-content-wrapper--campaign-list${
          showEmptyState ? " settings-content-wrapper--campaign-list-empty" : ""
        }`}
      >
        <Card className="table border-0 shadow-none rounded-lg campaign-list-card">
          <StandardTable
            fluidColCount={showEmptyState ? 1 : 5}
            fixedColWidth={showEmptyState ? 0 : 70}
            containerClassName={
              showEmptyState
                ? "table-standard-container--list-empty"
                : undefined
            }
            columnAlignments={[
              "left",
              "left",
              "left",
              "left",
              "left",
              "center",
            ]}
            topBar={
              showTableChrome ? (
                <section className="table-standard-top-bar campaign-list-top-bar">
                  <div className="table-standard-top-bar-inner">
                    <div className="flex gap-2 items-center flex-wrap flex-1 min-w-0">
                      <div className="search-input-wrapper campaign-list-search">
                        <i
                          className="fas fa-search search-input-icon"
                          aria-hidden
                        />
                        <input
                          type="text"
                          className="search-input"
                          value={searchValue}
                          placeholder={t("Search waste origin")}
                          onChange={(event) => {
                            setSearchValue(event.target.value);
                            setCurrentPage(1);
                          }}
                        />
                      </div>
                      <button
                        type="button"
                        className="search-input table-standard-filter-button"
                        onClick={() => setIsPeriodModalOpen(true)}
                      >
                        <span>
                          {t("Period")} :{" "}
                          <span className="font-semibold">
                            {periodDisplayLabel || t("Since the beginning")}
                          </span>
                        </span>
                        <i
                          className="fas fa-chevron-down table-standard-filter-icon"
                          aria-hidden
                        />
                      </button>
                    </div>
                  </div>
                  <CampaignStatusTabs
                    activeTab={statusTabFilter}
                    onTabChange={(tabId) => {
                      setStatusTabFilter(tabId);
                      setCurrentPage(1);
                    }}
                    counts={statusCounts}
                  />
                </section>
              ) : null
            }
            header={
              showTableChrome ? (
                <div className="table-standard-header">
                  <TableHeaderCell
                    label={t("Waste origin")}
                    columnKey="address"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                  />
                  <div className="table-standard-header-cell">{t("Zone")}</div>
                  <TableHeaderCell
                    label={t("Campaign date")}
                    columnKey="stop"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                  />
                  <TableHeaderCell
                    label={t("Status")}
                    columnKey="status"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                  />
                  <TableHeaderCell
                    label={t("campaign.sortingErrorsTreated")}
                    columnKey="errors"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                  />
                </div>
              ) : null
            }
            bottom={
              showTableChrome && sortedCampaigns.length > 0 ? (
                <div className="table-standard-bottom table-pagination-bottom">
                  <Pagination
                    totalEntry={sortedCampaigns.length}
                    recordPerPage={recordPerPage}
                    setRecordPerPage={setRecordPerPage}
                    currentPage={currentPage}
                    setCurrentPage={setCurrentPage}
                    resultPage={resultPage}
                    setResultPage={setResultPage}
                  />
                </div>
              ) : null
            }
          >
            {isLoading ? (
              <div className="table-standard-row">
                <div
                  className="table-standard-row-cell justify-center min-h-[200px]"
                  style={{ gridColumn: "1 / -1" }}
                >
                  <InlineLoader height="200px" />
                </div>
              </div>
            ) : hasCampaigns ? (
              paginatedCampaigns.map((campaign) => (
                <CampaignListRow
                  key={campaign._id}
                  campaign={campaign}
                  onEdit={handleEditCampaign}
                  onDuplicate={handleDuplicateCampaign}
                  onDelete={handleDeleteCampaign}
                />
              ))
            ) : hasActiveFilters ? (
              <div className="table-standard-row table-standard-row--empty">
                <div
                  className="table-standard-row-cell"
                  style={{ gridColumn: "span 5" }}
                >
                  {t("No results match your search")}
                </div>
                <div className="table-standard-row-cell table-standard-row-actions" />
              </div>
            ) : (
              <EmptyState
                title={t("emptyState.noCampaign")}
                description={t("emptyState.hintCampaign")}
                buttonText={t("Create a campaign")}
                onButtonClick={openCreatePage}
              />
            )}
          </StandardTable>
        </Card>
      </div>
      <PeriodFilterModal
        isOpen={isPeriodModalOpen}
        onClose={() => setIsPeriodModalOpen(false)}
        onApply={(months, label, startDate, endDate) => {
          setPeriodFilter(months ?? null);
          setPeriodDisplayLabel(label || t("Since the beginning"));
          setPeriodStartDate(startDate ?? null);
          setPeriodEndDate(endDate ?? null);
          setCurrentPage(1);
        }}
        currentPeriodFilter={periodFilter}
        currentPeriodStart={periodStartDate}
        currentPeriodEnd={periodEndDate}
      />
      {campaignToDelete ? (
        <CampaignDeleteModal
          campaign={campaignToDelete}
          campaignDelete={deleteModalOpen}
          setCampaignDelete={setDeleteModalOpen}
          fetchCampaignsPeriods={fetchCampaigns}
        />
      ) : null}
    </div>
  );
};

export default CampaignList;
